import mapboxgl from 'mapbox-gl'
import { Comment } from '../types/comments'
import { CommentMarker } from '../components/comments/CommentMarker'
import { createRoot } from 'react-dom/client'
import React from 'react'

export class CommentRenderer {
  private map: mapboxgl.Map
  private markers: Map<string, mapboxgl.Marker> = new Map()
  private onCommentClick?: (comment: Comment) => void
  private selectedCommentId?: string

  constructor(map: mapboxgl.Map) {
    this.map = map
  }

  /**
   * Set callback for when a comment marker is clicked
   */
  setOnCommentClick(callback: (comment: Comment) => void) {
    this.onCommentClick = callback
  }

  /**
   * Render comment markers on the map
   */
  renderComments(comments: Comment[], selectedCommentId?: string) {
    console.log('📍 CommentRenderer.renderComments called with:', comments.length, 'comments')
    this.selectedCommentId = selectedCommentId
    
    // Clear existing markers
    this.clearMarkers()

    // Filter only pinned comments with coordinates
    const pinnedComments = comments.filter(
      comment => comment.type === 'pinned' && comment.coordinates
    )
    console.log('📍 Filtered to', pinnedComments.length, 'pinned comments with coordinates')

    // Create markers for each pinned comment
    pinnedComments.forEach(comment => {
      console.log('📍 Creating marker for comment:', comment.id, 'at', comment.coordinates)
      this.createCommentMarker(comment)
    })
  }

  /**
   * Create a marker for a single comment
   */
  private createCommentMarker(comment: Comment) {
    if (!comment.coordinates) return

    // Create marker element
    const markerElement = document.createElement('div')
    markerElement.className = 'comment-marker'

    // Create React root and render CommentMarker component
    const root = createRoot(markerElement)
    root.render(
      React.createElement(CommentMarker, {
        comment,
        onClick: this.onCommentClick,
        isSelected: comment.id === this.selectedCommentId
      })
    )

    // Create Mapbox marker
    const marker = new mapboxgl.Marker({
      element: markerElement,
      anchor: 'center'
    })
      .setLngLat([comment.coordinates.lng, comment.coordinates.lat])
      .addTo(this.map)

    // Store marker for cleanup
    this.markers.set(comment.id, marker)
  }

  /**
   * Highlight a specific comment marker
   */
  highlightComment(commentId: string) {
    this.selectedCommentId = commentId
    
    // Re-render markers to update selection state
    // This is a bit inefficient but works for now
    // In production, you'd want to update just the affected markers
  }

  /**
   * Clear all comment markers
   */
  clearMarkers() {
    this.markers.forEach(marker => {
      marker.remove()
    })
    this.markers.clear()
  }

  /**
   * Show/hide comment markers based on filters
   */
  filterComments(comments: Comment[], filters: {
    categories?: string[]
    hideResolved?: boolean
  }) {
    let filteredComments = comments

    // Filter by categories
    if (filters.categories && filters.categories.length > 0) {
      filteredComments = filteredComments.filter(comment =>
        filters.categories!.includes(comment.category)
      )
    }

    // Filter resolved comments
    if (filters.hideResolved) {
      filteredComments = filteredComments.filter(comment => !comment.resolved)
    }

    this.renderComments(filteredComments, this.selectedCommentId)
  }

  /**
   * Get comments in current map viewport
   */
  getCommentsInViewport(comments: Comment[]): Comment[] {
    const bounds = this.map.getBounds()
    
    return comments.filter(comment => {
      if (!comment.coordinates) return false
      
      return bounds.contains([comment.coordinates.lng, comment.coordinates.lat])
    })
  }

  /**
   * Fly to a specific comment
   */
  flyToComment(comment: Comment, zoom = 15) {
    if (!comment.coordinates) return

    this.map.flyTo({
      center: [comment.coordinates.lng, comment.coordinates.lat],
      zoom,
      duration: 1000
    })

    // Highlight the comment
    this.highlightComment(comment.id)
  }

  /**
   * Add a temporary marker for placing new comments
   */
  addTemporaryMarker(coordinates: [number, number], onConfirm: (coords: [number, number]) => void) {
    // Create a draggable marker for new comment placement
    const markerElement = document.createElement('div')
    markerElement.innerHTML = `
      <div class="w-8 h-8 bg-blue-500 border-2 border-white rounded-full flex items-center justify-center cursor-pointer shadow-lg">
        <span class="text-white text-sm font-bold">+</span>
      </div>
    `

    const tempMarker = new mapboxgl.Marker({
      element: markerElement,
      draggable: true,
      anchor: 'center'
    })
      .setLngLat(coordinates)
      .addTo(this.map)

    // Add confirm button on click
    markerElement.addEventListener('click', () => {
      const lngLat = tempMarker.getLngLat()
      onConfirm([lngLat.lng, lngLat.lat])
      tempMarker.remove()
    })

    return tempMarker
  }

  /**
   * Cleanup
   */
  destroy() {
    this.clearMarkers()
  }
}